import { useEffect, useMemo, useState } from 'react';
import type { AgendaInputs } from '../assembly';
import type { Filters } from '../FilterBar';
import { fieldOf, type ObjectSummary } from '../format';
import { civil, mergeFilters } from '../list-state';
import { useCellRenderer, type Column } from './cells';
import { useCollection, type Row } from './useCollection';

/**
 * Renderer du composant Core `Agenda` (59 §7.2 ; ADR-048 §8 ; ADR-096 §3 ; ADR-099) : semaine ou mois ; la plage visible devient un
 * filtre `start_field gte / lt` sous la même recherche et les mêmes filtres que la liste. Un champ
 * `date` est filtré en dates civiles, un `datetime` par l'instant du minuit local (R1 ; le fuseau
 * du navigateur tient lieu de fuseau du site, comme la période d'ADR-094). Toute la plage est
 * chargée (jusqu'à 1 000 événements) ; un clic ouvre l'enregistrement.
 */

type Mode = 'week' | 'month';

const MAX_EVENTS = 1000;
const DAY_NAMES = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

function startOfWeek(date: Date): Date {
  const day = (date.getDay() + 6) % 7;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - day);
}

/** Plage affichée, fin exclue ; le mois s'affiche en semaines complètes. */
export function agendaRange(mode: Mode, anchor: Date): { from: Date; to: Date; days: Date[] } {
  const from = mode === 'week' ? startOfWeek(anchor) : startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  const lastOfMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const to = mode === 'week' ? new Date(from.getFullYear(), from.getMonth(), from.getDate() + 7) : (() => {
    const end = startOfWeek(lastOfMonth);
    return new Date(end.getFullYear(), end.getMonth(), end.getDate() + 7);
  })();
  const days: Date[] = [];
  for (let day = from; day < to; day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1)) days.push(day);
  return { from, to, days };
}

/** Jour civil d'une valeur de début : chaîne AAAA-MM-JJ telle quelle, instant ramené au jour local. */
function dayOf(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const instant = new Date(value);
  return Number.isNaN(instant.getTime()) ? null : civil(instant);
}

export function AgendaRenderer({
  type,
  object,
  agenda,
  listColumns,
  baseFilter,
  search,
  refreshKey,
  selectedId,
  onOpen,
}: {
  type: string;
  object: ObjectSummary | undefined;
  agenda: AgendaInputs;
  listColumns: readonly Column[];
  baseFilter: Filters;
  search: { q?: string; q_fields?: readonly string[] };
  refreshKey: number;
  selectedId: string | null;
  onOpen: (row: Row) => void;
}) {
  const [mode, setMode] = useState<Mode>(agenda.default_mode === 'month' ? 'month' : 'week');
  const [anchor, setAnchor] = useState(() => new Date());
  const range = useMemo(() => agendaRange(mode, anchor), [mode, anchor]);
  const startType = agenda.start_field === 'created_at' || agenda.start_field === 'updated_at' ? 'datetime' : fieldOf(object, agenda.start_field)?.type;
  const bound = (date: Date): string => (startType === 'date' ? civil(date) : date.toISOString());
  const filter = mergeFilters({ [agenda.start_field]: { gte: bound(range.from), lt: bound(range.to) } }, baseFilter);
  const collection = useCollection({ type, input: { filter, sort: [{ field: agenda.start_field, direction: 'asc' }], ...(search.q === undefined ? {} : { q: search.q, ...(search.q_fields === undefined ? {} : { q_fields: search.q_fields }) }) }, pageSize: 200 }, refreshKey);
  // Toute la plage : la suite est demandée tant qu'il en reste, dans la limite affichable.
  useEffect(() => {
    if (collection.status === 'ready' && !collection.done && !collection.loadingMore && collection.moreError === null && collection.rows.length < MAX_EVENTS) collection.loadMore();
  }, [collection]);
  const render = useCellRenderer(object, listColumns, [agenda.title_field], collection.rows, collection.meta);
  const byDay = new Map<string, Row[]>();
  for (const row of collection.rows) {
    const day = dayOf(row[agenda.start_field]);
    if (day === null) continue;
    byDay.set(day, [...(byDay.get(day) ?? []), row]);
  }
  const today = civil(new Date());
  const shift = (direction: -1 | 1): void =>
    setAnchor((current) => (mode === 'week' ? new Date(current.getFullYear(), current.getMonth(), current.getDate() + 7 * direction) : new Date(current.getFullYear(), current.getMonth() + direction, 1)));
  const title = mode === 'week'
    ? `Semaine du ${range.from.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
    : anchor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const timeOf = (row: Row): string | null => {
    const value = row[agenda.start_field];
    if (startType === 'date' || typeof value !== 'string' || /^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const instant = new Date(value);
    return Number.isNaN(instant.getTime()) ? null : instant.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`ay-agenda ay-agenda--${mode}`}>
      <div className="ay-agenda__bar">
        <div className="ay-agenda__nav">
          <button type="button" className="ay-button ay-button--secondary" onClick={() => shift(-1)} aria-label={mode === 'week' ? 'Semaine précédente' : 'Mois précédent'}>
            ‹
          </button>
          <button type="button" className="ay-button ay-button--secondary" onClick={() => setAnchor(new Date())}>
            Aujourd’hui
          </button>
          <button type="button" className="ay-button ay-button--secondary" onClick={() => shift(1)} aria-label={mode === 'week' ? 'Semaine suivante' : 'Mois suivant'}>
            ›
          </button>
        </div>
        <h2 className="ay-agenda__title" aria-live="polite">
          {title}
        </h2>
        <div className="ay-toggle-group" role="group" aria-label="Échelle">
          <button type="button" aria-pressed={mode === 'week'} onClick={() => setMode('week')}>
            Semaine
          </button>
          <button type="button" aria-pressed={mode === 'month'} onClick={() => setMode('month')}>
            Mois
          </button>
        </div>
      </div>
      {collection.status === 'error' ? (
        <div role="alert" className="ay-banner ay-banner--danger">
          {collection.error?.message}{' '}
          <button type="button" className="ay-button ay-button--secondary" onClick={collection.reload}>
            Réessayer
          </button>
        </div>
      ) : (
        <>
          {collection.rows.length >= MAX_EVENTS && !collection.done && <p className="ay-banner ay-banner--warning" role="status">Plus de {MAX_EVENTS.toLocaleString('fr')} événements sur la période : restreindre les filtres.</p>}
          <div className="ay-agenda__grid" aria-busy={collection.status === 'loading' || collection.loadingMore}>
            {DAY_NAMES.map((name) => (
              <div key={name} className="ay-agenda__dayname" aria-hidden="true">
                {name}
              </div>
            ))}
            {range.days.map((day) => {
              const key = civil(day);
              const events = byDay.get(key) ?? [];
              const outside = mode === 'month' && day.getMonth() !== anchor.getMonth();
              return (
                <section key={key} className={`ay-agenda__day${key === today ? ' ay-agenda__day--today' : ''}${outside ? ' ay-agenda__day--outside' : ''}`} aria-label={day.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}>
                  <span className="ay-agenda__date">{day.getDate()}</span>
                  <ul className="ay-agenda__events">
                    {events.map((row, index) => {
                      const id = typeof row.id === 'string' ? row.id : `${key}-${index}`;
                      const time = timeOf(row);
                      return (
                        <li key={id}>
                          <button type="button" data-opens-record className={`ay-agenda__event${selectedId === id ? ' ay-agenda__event--selected' : ''}`} onClick={() => onOpen(row)}>
                            {time !== null && <span className="ay-agenda__time">{time}</span>}
                            {render.display(agenda.title_field, row)}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
